// src/features/knowledge/ReflectionGlobalIndicator.tsx
// Toast e Indicador Global de Reflexão Interna da IA em todas as telas do CRM

import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Zap, X, ArrowRight, Sparkles } from 'lucide-react';
import { useSessionStore } from '../../stores/sessionStore';
import { fetchLiveReflectionTrace, ReflectionTrace } from './knowledgeGraphApi';

export function ReflectionGlobalIndicator() {
  const token = useSessionStore((state) => state.session?.access_token);
  const navigate = useNavigate();
  const [currentTrace, setCurrentTrace] = useState<ReflectionTrace | null>(null);
  const lastTimestampRef = useRef<number>(Math.floor(Date.now() / 1000));
  const dismissTimerRef = useRef<any>(null);

  useEffect(() => {
    if (!token) return;

    const interval = setInterval(async () => {
      try {
        const trace = await fetchLiveReflectionTrace(token, lastTimestampRef.current);
        if (trace && trace.id && trace.createdAt > lastTimestampRef.current) {
          lastTimestampRef.current = trace.createdAt;
          setCurrentTrace(trace);

          // Auto dismiss após 8s
          if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
          dismissTimerRef.current = setTimeout(() => {
            setCurrentTrace(null);
          }, 8000);
        }
      } catch {
        // Polling silencioso
      }
    }, 4500);

    return () => {
      clearInterval(interval);
      if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
    };
  }, [token]);

  if (!currentTrace) return null;

  const nodeCount = currentTrace.activatedNodeIds?.length || currentTrace.activationPath?.length || 0;
  const confidencePct = Math.round((currentTrace.decisionConfidence || 0.8) * 100);

  const handleOpenBrain = () => {
    setCurrentTrace(null);
    navigate(`/knowledge?traceId=${currentTrace.id}`);
  };

  return (
    <aside className="reflection-toast-container" role="status" aria-live="polite">
      <div className="reflection-synapse-pulse" title="Sinapse Neural Ativa">
        <Brain size={15} className="text-white" />
      </div>

      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="font-semibold text-purple-300 text-xs flex items-center gap-1">
            <Sparkles size={12} className="text-yellow-400" />
            Reflexão Interna da IA
          </span>
          <span className="text-[10px] px-1.5 py-0.2 rounded bg-purple-950/80 border border-purple-500/30 text-purple-200">
            {confidencePct}% certeza
          </span>
        </div>

        <p className="text-xs text-slate-300 max-w-[320px] truncate m-0 font-normal">
          {currentTrace.actionSummary}
        </p>

        {nodeCount > 0 && (
          <span className="text-[11px] text-slate-400 flex items-center gap-1">
            <Zap size={10} className="text-cyan-400" />
            Consultou {nodeCount} {nodeCount === 1 ? 'neurônio' : 'neurônios'} no cérebro
          </span>
        )}
      </div>

      <button
        onClick={handleOpenBrain}
        className="reflection-view-brain-btn"
        title="Visualizar o caminho neural no Conectoma 3D"
      >
        <span>Ver no Cérebro</span>
        <ArrowRight size={12} />
      </button>

      <button
        onClick={() => setCurrentTrace(null)}
        className="text-slate-400 hover:text-white p-1 ml-1 rounded-full transition-colors"
        aria-label="Fechar notificação de reflexão"
      >
        <X size={14} />
      </button>
    </aside>
  );
}
